import * as yup from 'yup';

import { Card, CardBody, CardFooter, Form, FormGroup, Input, Label } from 'reactstrap';
import { CoaDto, ProfessionDto } from '../common/Types';
import React, { useEffect, useState } from 'react';

import { Button } from '.';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { coaService } from '../services/CoaAdministration';
import { professionService } from '../services/ProfessionAdministration';

interface NewProfessionFormProps {
    onCancel: () => void;
    onSave: (newProfessionId: number) => void;
}

const NewProfessionForm = (props: NewProfessionFormProps) => {
    const { onCancel, onSave } = props;

    const [coas, setCoas] = useState<CoaDto[]>();

    const newProfessionSchema = yup
        .object<ProfessionDto>({
            professionId: yup.number().required().default(0),
            title: yup.string().required(`Profession Name is required`),
            isDeleted: yup.boolean().required().default(false),
            webSiteSearchCount: yup.number().required().default(0),
            armsUser: yup.boolean().required().default(true),
            showDegreeOnCertificate: yup.boolean().required().default(false),
            coaId: yup.number().required().min(1, `Please select a CoA`),
            coaTitle: yup.string().required().default(''),
        })
        .defined();

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(newProfessionSchema as any),
        defaultValues: {} as ProfessionDto,
    });

    useEffect(() => {
        if (!coas) {
            coaService
                .getAllCoas()
                .then((results) => {
                    setCoas(
                        results.filter((r) => r.isActive === true).sort((a, b) => (a.abb || '').localeCompare(b.abb)),
                    );
                })
                .catch(() => {
                    toast.error('Error loading CoA list');
                });
        }
    }, [setCoas, coas]);

    const onSubmit = (values: ProfessionDto) => {
        const toastId = toast.info('Creating profession...');

        professionService
            .saveProfessions([values])
            .then((results) => {
                if (!results || results.length === 0 || !results[0]) {
                    toast.update(toastId, {
                        render: 'Error creating profession: No result returned',
                        type: 'error',
                    });
                    return;
                }
                professionService
                    .createArmsProfession({
                        ...results[0],
                    })
                    .then((result) => {
                        if (!result || !result.professionId) {
                            toast.update(toastId, {
                                render: 'Error creating profession: No ARMS result returned',
                                type: 'error',
                            });
                            return;
                        }
                        toast.update(toastId, {
                            render: 'Profession created successfully',
                            type: 'success',
                        });

                        onSave(result.professionId);
                    })
                    .catch(() => {
                        toast.update(toastId, {
                            render: 'Error creating profession',
                            type: 'error',
                        });
                    });
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error creating profession',
                    type: 'error',
                });
            });
    };

    return (
        <>
            {coas && (
                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <Card className={'mb-0'}>
                        <CardBody>
                            <h4 className={'card-title'}>{`New Profession`}</h4>

                            <FormGroup>
                                <Label>{`Profession Name`}</Label>

                                <Controller
                                    name="title"
                                    control={control}
                                    render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                />
                                {errors?.title?.message && (
                                    <span className={'text-danger mt-2'}>{errors.title.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`CoA`}</Label>

                                <Controller
                                    name="coaId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type={'select'} {...field} value={field.value ?? ''}>
                                            <option value={-1}>{`Select a CoA`}</option>
                                            {coas.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.abb}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                />
                                <Controller
                                    name="coaTitle"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type={'hidden'} {...field} value={field.value ?? 'N/A'} />
                                    )}
                                />
                                {errors?.coaId?.message && (
                                    <span className={'text-danger mt-2'}>{errors.coaId.message}</span>
                                )}
                            </FormGroup>
                        </CardBody>
                        <CardFooter>
                            <Button color={'primary'} type={'submit'}>
                                <span>{`Save`}</span>
                            </Button>
                            <Button color={'secondary'} className={'ml-2'} onClick={onCancel}>
                                <span>{`Cancel`}</span>
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            )}
        </>
    );
};

export default NewProfessionForm;
